// Kalender nach Mitarbeitern filtern und Abwesenheiten zeigen (Ticket #506, Björn 16.10.2026):
// „Mitarbeiter sollen untereinander sehen, wann wer wo Termine hat — für Rückfragen, oder
// um Urlaub zu sehen, wenn jemand etwas einträgt."
//
// Entscheidungen Patrick (16.10.2026):
//  - Abwesenheiten erscheinen nur, wenn sie GENEHMIGT sind.
//  - Der Grund steht dabei, AUSSER bei Gesundheit: Krank und Arzttermin erscheinen nur als
//    „abwesend". Gesundheitsangaben gehören nicht in einen Kalender, den alle sehen. Unbezahlter
//    Urlaub ebenso — der Grund dafür ist privat.

export interface KalenderPerson {
  id: string;
  full_name: string;
  email: string | null;
}

export interface TerminMitPersonen {
  zustaendig_id?: string | null;
  teilnehmer?: { email: string }[] | null;
}

/**
 * Gehört der Termin zu einer der gewählten Personen? Ohne Auswahl gehört jeder Termin dazu.
 * Zuständig zählt über die Profil-Id; Teilnehmer kommen aus Outlook und tragen nur eine
 * E-Mail-Adresse — die wird über die Profile einer Person zugeordnet.
 */
export function terminFuerPersonen(
  t: TerminMitPersonen,
  ausgewaehlt: ReadonlySet<string>,
  idZuMail: ReadonlyMap<string, string>,
): boolean {
  if (ausgewaehlt.size === 0) return true;
  if (t.zustaendig_id && ausgewaehlt.has(t.zustaendig_id)) return true;
  const mails = new Set<string>();
  ausgewaehlt.forEach((id) => { const m = idZuMail.get(id); if (m) mails.add(m); });
  return (t.teilnehmer ?? []).some((p) => mails.has((p.email || '').trim().toLowerCase()));
}

/**
 * Wessen Farbe ein Termin trägt (#615, gleiche Farben wie bei den Mails): der Zuständige, sonst der
 * erste Teilnehmer, der ein Mitarbeiter ist (Outlook-Termine wie „Urlaub Selina" haben oft keinen
 * Zuständigen). Null, wenn niemand aus dem Team dabei ist.
 */
export function terminPersonId(t: TerminMitPersonen, idZuMail: ReadonlyMap<string, string>): string | null {
  if (t.zustaendig_id) return t.zustaendig_id;
  const mailZuId = new Map<string, string>();
  idZuMail.forEach((mail, id) => mailZuId.set(mail, id));
  for (const p of t.teilnehmer ?? []) {
    const id = mailZuId.get((p.email || '').trim().toLowerCase());
    if (id) return id;
  }
  return null;
}

/** Profil-Id → kleingeschriebene E-Mail, für den Abgleich mit Outlook-Teilnehmern. */
export function mailVerzeichnis(personen: readonly KalenderPerson[]): Map<string, string> {
  const m = new Map<string, string>();
  personen.forEach((p) => { if (p.email?.trim()) m.set(p.id, p.email.trim().toLowerCase()); });
  return m;
}

export type AbwesenheitTyp =
  | 'urlaub' | 'krank' | 'fza' | 'homeoffice' | 'dienstreise' | 'sonderurlaub' | 'unbezahlt' | 'arzttermin';

/** Was im Kalender steht. Gesundheit und Privates nur als „abwesend". */
export const ABWESENHEIT_TEXT: Record<AbwesenheitTyp, string> = {
  urlaub: 'Urlaub',
  sonderurlaub: 'Sonderurlaub',
  homeoffice: 'Homeoffice',
  dienstreise: 'Dienstreise',
  fza: 'Freizeitausgleich',
  krank: 'abwesend',
  arzttermin: 'abwesend',
  unbezahlt: 'abwesend',
};

export interface AbwesenheitRoh {
  id: string;
  mitarbeiter_id: string;
  typ: string;
  von_tag: string;
  bis_tag: string;
  halber_tag?: boolean | null;
  status: string;
}

export interface AbwesenheitEintrag {
  id: string;
  mitarbeiterId: string;
  name: string;
  text: string;
  halberTag: boolean;
}

function naechsterTag(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Abwesenheiten je Tag (YYYY-MM-DD) im Bereich [von, bis). Nur genehmigte; mit Personenauswahl
 * nur die der gewählten Personen. Mehrtägige Abwesenheiten erscheinen an jedem Tag.
 */
export function abwesenheitenJeTag(
  liste: readonly AbwesenheitRoh[],
  namen: ReadonlyMap<string, string>,
  ausgewaehlt: ReadonlySet<string>,
  von: string,
  bis: string,
): Map<string, AbwesenheitEintrag[]> {
  const m = new Map<string, AbwesenheitEintrag[]>();
  for (const a of liste) {
    if (a.status !== 'genehmigt') continue;
    if (ausgewaehlt.size > 0 && !ausgewaehlt.has(a.mitarbeiter_id)) continue;
    const eintrag: AbwesenheitEintrag = {
      id: a.id,
      mitarbeiterId: a.mitarbeiter_id,
      name: namen.get(a.mitarbeiter_id) || 'Mitarbeiter',
      text: ABWESENHEIT_TEXT[a.typ as AbwesenheitTyp] ?? 'abwesend',
      halberTag: !!a.halber_tag,
    };
    let tag = a.von_tag < von ? von : a.von_tag;
    let sicherung = 0;
    while (tag <= a.bis_tag && tag < bis && sicherung++ < 400) {
      m.set(tag, [...(m.get(tag) ?? []), eintrag]);
      tag = naechsterTag(tag);
    }
  }
  m.forEach((eintraege) => eintraege.sort((a, b) => a.name.localeCompare(b.name, 'de')));
  return m;
}

/** Vorname für die knappen Kalenderzellen („Saskia Damm" → „Saskia"). */
export function vorname(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

// ── Wo ist jemand gerade? ───────────────────────────────────────────────────────
// Patrick 16.10.2026: Wer einen Anruf annimmt, soll sofort sehen, ob Björn gerade im Termin
// ist — ohne im Monat zu suchen. Deshalb zeigt jeder Mitarbeiter-Knopf seinen Stand JETZT.

export interface TerminZeit extends TerminMitPersonen {
  titel?: string | null;
  start_datum: string;
  end_datum?: string | null;
  ganztaegig?: boolean | null;
  status?: string | null;
}

export type JetztStatus =
  | { art: 'abwesend'; text: string }
  | { art: 'termin'; text: string; bis: Date | null }
  | { art: 'frei'; text: string };

const uhrzeit = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export function jetztStatus(
  personId: string,
  heuteTermine: readonly TerminZeit[],
  heuteAbwesend: readonly AbwesenheitEintrag[],
  idZuMail: ReadonlyMap<string, string>,
  jetzt: Date,
): JetztStatus {
  const abw = heuteAbwesend.find((a) => a.mitarbeiterId === personId);
  if (abw && !abw.halberTag) return { art: 'abwesend', text: abw.text === 'abwesend' ? 'abwesend' : abw.text };

  const meine = heuteTermine
    .filter((t) => t.status !== 'abgesagt' && terminFuerPersonen(t, new Set([personId]), idZuMail))
    .map((t) => ({ t, start: new Date(t.start_datum), ende: t.end_datum ? new Date(t.end_datum) : null }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const laufend = meine.find(({ t, start, ende }) =>
    t.ganztaegig ? true : start <= jetzt && (ende ? jetzt < ende : jetzt.getTime() - start.getTime() < 60 * 60 * 1000));
  if (laufend) {
    if (laufend.t.ganztaegig) return { art: 'termin', text: 'ganztägig im Termin', bis: null };
    return laufend.ende
      ? { art: 'termin', text: `im Termin bis ${uhrzeit(laufend.ende)}`, bis: laufend.ende }
      : { art: 'termin', text: 'im Termin', bis: null };
  }
  if (abw?.halberTag) return { art: 'abwesend', text: `halber Tag ${abw.text}` };
  const naechster = meine.find(({ start }) => start > jetzt);
  return naechster
    ? { art: 'frei', text: `frei · nächster Termin ${uhrzeit(naechster.start)}` }
    : { art: 'frei', text: 'frei' };
}
